/*
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	This code is unlicensed and may not be copied, used, modified,
 **	compiled, run, or distributed in any form without the explicit
 **	written permission of Silver Falls Capital.
 **
 **	Author(s): Claude Code Assistant
 **
 */

import Config from './config'
import { apiTokenConfiguration } from './apiConfig'
import { DevicesApi, DeviceDataApi, LocationsApi, NamespaceApi, DeviceEventsApi, DeviceCommandsApi } from '@silverfallscapital/strata47-typescript-sdk'
import type { ListDevicesRequest, ListDeviceDataRequest, ListLocationsRequest, ListDeviceEventsRequest, DeviceEvent, DeviceCommandCreateUpdate, DeviceCreateUpdate } from '@silverfallscapital/strata47-typescript-sdk'

// GraphQL Query Helper (for escalations only)
async function graphqlQuery(query: string, variables: any = {}, userToken: string): Promise<any> {
  // Strip auth0| prefix if present using apiTokenConfiguration logic
  const cleanToken = userToken.startsWith("auth0|")
    ? userToken.split("|")[1]
    : userToken.replace("strata47|", "")

  console.log('🌐 GraphQL Request:', {
    endpoint: Config.STRATA47_OMNIIQ_API,
    variables: variables,
    tokenLength: cleanToken?.length,
    tokenPrefix: cleanToken?.substring(0, 20) + '...',
    hadPrefix: userToken.startsWith("auth0|") || userToken.startsWith("strata47|")
  })

  const response = await fetch(Config.STRATA47_OMNIIQ_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${cleanToken}`,
    },
    body: JSON.stringify({ query, variables }),
  })

  console.log('🌐 GraphQL Response Status:', response.status, response.statusText)

  if (!response.ok) {
    const responseText = await response.text()
    console.error('❌ GraphQL Error Response:', responseText)
    console.error('❌ Response Status:', response.status)
    console.error('❌ Response Headers:', JSON.stringify([...response.headers.entries()]))
    throw new Error(`GraphQL request failed: ${response.status}`)
  }

  const result = await response.json()
  console.log('🌐 GraphQL Response Body:', JSON.stringify(result, null, 2))

  if (result.errors) {
    console.error('❌ GraphQL errors:', result.errors)
    console.error('❌ GraphQL variables:', variables)
    throw new Error(result.errors[0]?.message || 'GraphQL query failed')
  }

  return result.data
}

// Types
export interface Device {
  deviceId: string
  name?: string  // This is the actual field from Strata47 API
  deviceTypeId?: string
  locationId?: string
  createdAt: string
  updatedAt: string
  lastSeen?: string
  isActive?: boolean
}

export interface DeviceData {
  deviceId: string
  timestamp: string
  dp0?: number // Primary pressure
  dp1?: number // Secondary pressure
  dp0wt?: number // Warning threshold 
  dp0at?: number // Alarm threshold
  dp1wt?: number // Secondary warning threshold
  dp1at?: number // Secondary alarm threshold
  dp0_alarm_state?: number
  dp1_alarm_state?: number
  w_rssi?: number
  c_rssi?: number
  measure_unit?: number
  br_ping_ct?: number
  br_bad_ping_ct?: number
  br_bad_ping_seq_ct?: number
  br_longest_bad_ping_seq_ct?: number
}

export interface Location {
  locationId: string
  name: string
  description: string
  address?: string
  lat?: number
  _long?: number
  parentLocIds?: string[]
  createdAt: string
  updatedAt: string
}

export interface User {
  userId: string
  email: string
  firstName?: string
  lastName?: string
  phone?: string
  timezone?: string
  organizationId?: string
  organizationName?: string
}

export interface Alarm {
  id: string
  deviceId: string
  deviceName: string
  alarmType: 'Alarm' | 'Warning' | 'Offline'
  status: 'active' | 'acknowledged' | 'muted' | 'resolved'
  message: string
  timestamp: string
  acknowledgedBy?: string
  acknowledgedAt?: string
  resolvedAt?: string
}

export interface DeviceEventData {
  eventId: string
  deviceId: string
  deviceName?: string
  level: string  // info, warning, error, alarm
  message: string
  timestamp: string  
  locationId?: string
  locationName?: string
  deviceTypeId?: string
  deviceTypeName?: string
}

// Generic REST API request function (fallback for non-SDK APIs)
// async function apiRequest<T>(
//   endpoint: string,
//   token: string,
//   options: RequestInit = {}
// ): Promise<T> {
//   const response = await fetch(`${Config.STRATA47_API_URL}${endpoint}`, {
//     ...options,
//     headers: {
//       'Authorization': `Bearer ${token}`,
//       'Content-Type': 'application/json',
//       ...options.headers,
//     },
//   })

//   if (!response.ok) {
//     throw new Error(`API request failed: ${response.status} ${response.statusText}`)
//   }

//   const data = await response.json()
//   return data
// }

// Device API functions (using Strata47 SDK)
export async function getAllDevices(token: string): Promise<Device[]> {
  try {
    const apiInstance = new DevicesApi(apiTokenConfiguration(token))
    const req: ListDevicesRequest = {
      perPage: 1000, // Get all devices
    }
    const response = await apiInstance.listDevices(req)

    // Check if response is directly an array or has items property
    if (Array.isArray(response)) {
      return response.filter(device => device.deviceId) as unknown as Device[]
    } else if (response?.items) {
      return response.items.filter((device: any) => device.deviceId) as unknown as Device[]
    } else {
      console.warn('❌ getAllDevices: No valid device data found')
      return []
    }
  } catch (error) {
    console.error('Failed to fetch devices:', error)
    return []
  }
}

export async function getDeviceById(deviceId: string, token: string): Promise<any> {
  try {
    const apiInstance = new DevicesApi(apiTokenConfiguration(token))
    const response = await apiInstance.getDeviceById({ deviceId })
    return response
  } catch (error) {
    console.error(`Failed to fetch device ${deviceId}:`, error)
    throw error
  }
}

// Update device by ID (iOS-compatible direct fetch implementation)
// The SDK's updateDeviceById method has issues on iOS WebView, so we use direct fetch
export async function updateDeviceById(
  deviceId: string,
  updateData: DeviceCreateUpdate,
  token: string
): Promise<any> {
  try {
    // Use same config pattern as SDK
    const config = apiTokenConfiguration(token)
    const tokenValue = token.startsWith('auth0|')
      ? token.split('|')[1]
      : token.replace('strata47|', '')

    console.log('📝 Updating device via direct fetch:', { deviceId, updateData })

    // Make direct fetch call with SDK-style headers
    // Note: Using PUT instead of PATCH. Config.STRATA47_API_URL already includes /v1
    const response = await fetch(`${Config.STRATA47_API_URL}/device/${deviceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokenValue}`,
        'x-bp-token': config.headers?.['x-bp-token'] as string || '',
        'X-Client-Type': 'omniiq-mobile-client',
      },
      body: JSON.stringify(updateData),
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Device update failed:', response.status, errorText)
      throw new Error(`Update failed: ${response.status} - ${errorText}`)
    }

    const result = await response.json()
    console.log('✅ Device updated successfully:', result)
    return result
  } catch (error) {
    console.error('Failed to update device:', error)
    throw error
  }
}

export async function getDeviceData(
  deviceId: string, 
  token: string, 
  createdAfter?: Date,
  latest = true
): Promise<DeviceData[]> {
  try {
    const apiInstance = new DeviceDataApi(apiTokenConfiguration(token))
    const req: ListDeviceDataRequest = {
      deviceId: deviceId,
      ...(createdAfter && { createdAfter }),
      ...(latest && { latest: 'true' })
    }
    const response = await apiInstance.listDeviceData(req)
    
    // Return the items array, or empty array if no items or response is null
    if (Array.isArray(response)) {
      return response
    } else if (response && 'items' in response && Array.isArray((response as any).items)) {
      return (response as any).items
    } else {
      console.warn(`No valid device data found for device ${deviceId}`)
      return []
    }
  } catch (error) {
    console.error(`Failed to fetch device data for ${deviceId}:`, error)
    return []
  }
}

// Location API functions (using Strata47 SDK)
export async function getAllLocations(token: string): Promise<Location[]> {
  try {
    const apiInstance = new LocationsApi(apiTokenConfiguration(token))
    const req: ListLocationsRequest = {
      perPage: 1000, // Get all locations
    }
    
    const response = await apiInstance.listLocations(req)
    return (response.items || []).filter((location: any) => location.locationId) as unknown as Location[]
  } catch (error) {
    console.error('Failed to fetch locations:', error)
    return []
  }
}

// Device Commands API
export async function updateDeviceSettings(
  deviceId: string,
  _token: string,
  settings: {
    warningThreshold?: number
    alarmThreshold?: number
    readingPeriod?: number // in seconds
  }
): Promise<boolean> {
  try {
    // For now, return true - device settings update would use Strata47 SDK
    // This would need to be implemented based on Strata47 device management API
    console.log('Device settings update not yet implemented:', deviceId, settings)
    return true
  } catch (error) {
    console.error(`Failed to update device settings for ${deviceId}:`, error)
    return false
  }
}

// Get current user's namespace information from Strata47
export async function getCurrentUserNamespace(token: string): Promise<{ id: string, name: string, title?: string } | null> {
  try {
    const config = apiTokenConfiguration(token)
    
    // First try to get the list of namespaces the user has access to
    console.log('🔗 Attempting to get namespaces from Strata47...')
    const namespaceApi = new NamespaceApi(config)
    const namespacesResponse = await namespaceApi.listNamespaces()
    
    console.log('🔗 Strata47 Namespaces Response:', namespacesResponse)
    console.log('🔗 Response type:', typeof namespacesResponse)
    console.log('🔗 Response keys:', Object.keys(namespacesResponse))
    
    // Handle different possible response structures
    let namespaces = null
    
    if (namespacesResponse && typeof namespacesResponse === 'object') {
      if ('items' in namespacesResponse && Array.isArray((namespacesResponse as any).items)) {
        namespaces = (namespacesResponse as any).items
        console.log('🔗 Found namespaces array with length:', namespaces.length)
      } else if (Array.isArray(namespacesResponse)) {
        namespaces = namespacesResponse
        console.log('🔗 Response is direct array with length:', namespaces.length)
      } else {
        // Response might be a single namespace object
        namespaces = [namespacesResponse]
        console.log('🔗 Response is single namespace object')
      }
    }
    
    if (namespaces && namespaces.length > 0) {
      // Take the first namespace the user has access to
      const namespace = namespaces[0]
      console.log('🔗 First namespace data:', namespace)
      console.log('🔗 Namespace keys:', Object.keys(namespace))
      
      // Look for ID and name fields in the namespace object
      const possibleIdFields = ['id', 'namespaceId', 'namespace_id', 'ns_id']
      const possibleNameFields = ['name', 'namespaceName', 'namespace_name', 'title', 'displayName']
      
      let namespaceId = null
      let namespaceName = null
      
      // Try to find namespace ID field
      for (const field of possibleIdFields) {
        if (namespace[field]) {
          namespaceId = namespace[field]
          console.log(`🔗 Found namespace ID in field '${field}':`, namespaceId)
          break
        }
      }
      
      // Try to find namespace name field
      for (const field of possibleNameFields) {
        if (namespace[field]) {
          namespaceName = namespace[field]
          console.log(`🔗 Found namespace name in field '${field}':`, namespaceName)
          break
        }
      }
      
      if (namespaceId) {
        return {
          id: String(namespaceId),
          name: namespaceName || 'omniiq-namespace',
          title: namespaceName || 'Omni IQ Namespace'
        }
      }
    }
    
    console.warn('🔗 No namespace information found in response')
    console.warn('🔗 Available fields in response:', namespaces ? Object.keys(namespaces[0] || {}) : 'No namespace data')
    return null
  } catch (error) {
    console.error('🔗 Failed to get namespace from Strata47:', error)
    console.error('🔗 Error details:', error instanceof Error ? error.message : String(error))
    return null
  }
}

// User API functions - Note: User profile management not available in mobile app
// Use Auth0 user info for basic user data
export async function getUserProfile(_token: string): Promise<User | null> {
  // For mobile app, we don't access Strata47 user profile API directly
  // Instead, we should use Auth0 user information or GraphQL user queries
  console.warn('getUserProfile not implemented in mobile app - use Auth0 user info')
  return null
}

export async function updateUserProfile(
  _token: string,
  _updates: Partial<User>
): Promise<boolean> {
  console.warn('updateUserProfile not implemented in mobile app')
  return false
}

export async function changePassword(
  _token: string,
  _currentPassword: string,
  _newPassword: string
): Promise<boolean> {
  console.warn('changePassword not implemented in mobile app')
  return false
}

// Alarm API functions - using alarms query from GraphQL schema
export async function getAlarms(token: string, status?: string, namespaceId?: string): Promise<Alarm[]> {
  try {
    // namespaceId is required for multi-tenant design
    if (!namespaceId) {
      console.warn('⚠️ getAlarms called without namespaceId - this violates multi-tenant design. No alarms will be returned.')
      return []
    }

    // Determine isResolved filter based on status
    let isResolved: boolean | undefined
    if (status === 'resolved') {
      isResolved = true
    } else if (status === 'active' || status === 'acknowledged') {
      isResolved = false
    }
    // If status is undefined, fetch all alarms (resolved and unresolved)

    const query = `
      query GetAlarms($namespaceId: String!, $isResolved: Boolean) {
        alarms(namespaceId: $namespaceId, isResolved: $isResolved) {
          id
          deviceId
          deviceName
          alarmType
          alarmStartedAt
          alarmEndedAt
          locationId
          locationPath
          alarmValue
          thresholdValue
          alarmSeverity
          isAcknowledged
          acknowledgedBy
          acknowledgedAt
          acknowledgmentMethod
          acknowledgmentNote
          isResolved
          resolvedBy
          resolvedAt
          resolutionProblemDescription
          resolutionSolutionDescription
          wasEscalated
          escalatedAt
        }
      }
    `

    const variables: any = { namespaceId }
    if (isResolved !== undefined) {
      variables.isResolved = isResolved
    }

    const result = await graphqlQuery(query, variables, token)

    // Transform alarms from GraphQL to match Alarm interface
    const alarms = result.alarms || []
    let filteredAlarms = alarms

    // Further filter by status if needed (for acknowledged vs active)
    if (status === 'acknowledged') {
      filteredAlarms = alarms.filter((a: any) => a.isAcknowledged && !a.isResolved)
    } else if (status === 'active') {
      filteredAlarms = alarms.filter((a: any) => !a.isAcknowledged && !a.isResolved)
    }

    return filteredAlarms.map((alarm: any) => ({
      id: alarm.id,
      deviceId: alarm.deviceId,
      deviceName: alarm.deviceName || `Device ${alarm.deviceId}`,
      alarmType: alarm.alarmType === 'dp0_alarm' ? 'Alarm' : (alarm.alarmType === 'dp1_alarm' ? 'Alarm' : 'Offline'),
      status: alarm.isResolved ? 'resolved' : (alarm.isAcknowledged ? 'acknowledged' : 'active'),
      message: `${alarm.alarmType} from ${alarm.locationPath || 'Unknown Location'}`,
      timestamp: alarm.alarmStartedAt,
      acknowledgedBy: alarm.acknowledgedBy,
      acknowledgedAt: alarm.acknowledgedAt,
      resolvedAt: alarm.resolvedAt,
    }))
  } catch (error) {
    console.error('Failed to fetch alarms:', error)
    return []
  }
}

// Alarm Response Tracking
export interface AlarmResolution {
  problemDescription: string
  resolutionDescription: string
}

// Create alarm function for fallback when alarm doesn't exist yet
export async function createAlarm(
  token: string,
  namespaceId: string,
  deviceId: string,
  alarmType: string,
  deviceData: DeviceData,
  deviceName?: string
): Promise<string | null> {
  try {
    const mutation = `
      mutation CreateOrUpdateAlarm($input: CreateAlarmInput!) {
        createOrUpdateAlarm(input: $input) {
          id
          deviceId
          alarmType
          isResolved
        }
      }
    `

    const input = {
      namespaceId,
      deviceId,
      alarmType,
      alarmStartedAt: new Date(deviceData.timestamp || Date.now()).toISOString(),
      deviceName,
      alarmValue: alarmType === 'dp0_alarm' ? deviceData.dp0 : deviceData.dp1,
      thresholdValue: alarmType === 'dp0_alarm' ? deviceData.dp0at : deviceData.dp1at,
      alarmSeverity: 'alarm'
    }

    const result = await graphqlQuery(mutation, { input }, token)

    if (result.createOrUpdateAlarm?.id) {
      console.log(`✅ Created alarm record: ${result.createOrUpdateAlarm.id}`)
      return result.createOrUpdateAlarm.id
    }

    return null
  } catch (error) {
    console.error('❌ Failed to create alarm:', error)
    return null
  }
}

export async function acknowledgeEscalation(
  alarmId: string,
  token: string,
  note?: string
): Promise<boolean> {
  try {
    const mutation = `
      mutation AcknowledgeAlarm($input: AcknowledgeAlarmInput!) {
        acknowledgeAlarm(input: $input) {
          id
          isAcknowledged
          acknowledgedBy
          acknowledgedAt
          acknowledgmentNote
        }
      }
    `

    const input = {
      alarmId,
      acknowledgmentNote: note,
      acknowledgmentMethod: 'mobile'
    }

    await graphqlQuery(mutation, { input }, token)
    console.log(`✅ Successfully acknowledged alarm ${alarmId}`)
    return true
  } catch (error) {
    console.error(`❌ Failed to acknowledge alarm ${alarmId}:`, error)
    return false
  }
}

export async function resolveEscalation(
  alarmId: string,
  resolution: AlarmResolution,
  token: string
): Promise<boolean> {
  try {
    const mutation = `
      mutation ResolveAlarm($input: ResolveAlarmInput!) {
        resolveAlarm(input: $input) {
          id
          isAcknowledged
          isResolved
          resolvedBy
          resolvedAt
          resolutionProblemDescription
          resolutionSolutionDescription
        }
      }
    `

    const input = {
      alarmId,
      resolutionProblemDescription: resolution.problemDescription,
      resolutionSolutionDescription: resolution.resolutionDescription
    }

    await graphqlQuery(mutation, { input }, token)
    console.log(`✅ Successfully resolved alarm ${alarmId}`)
    return true
  } catch (error) {
    console.error(`❌ Failed to resolve alarm ${alarmId}:`, error)
    return false
  }
}

// Legacy function - kept for backward compatibility
export async function acknowledgeAlarm(alarmId: string, token: string): Promise<boolean> {
  return acknowledgeEscalation(alarmId, token)
}

export async function muteAlarm(alarmId: string, token: string, duration?: number): Promise<boolean> {
  try {
    const mutation = `
      mutation MuteAlarm($alarmId: String!, $duration: Int) {
        muteAlarm(alarmId: $alarmId, duration: $duration) {
          id
          status
        }
      }
    `
    
    const variables: any = { alarmId }
    if (duration) {
      variables.duration = duration
    }
    
    await graphqlQuery(mutation, variables, token)
    return true
  } catch (error) {
    console.error(`Failed to mute alarm ${alarmId}:`, error)
    return false
  }
}

export async function escalateAlarm(alarmId: string, token: string): Promise<boolean> {
  try {
    const mutation = `
      mutation EscalateAlarm($alarmId: String!) {
        escalateAlarm(alarmId: $alarmId) {
          id
          status
        }
      }
    `
    
    await graphqlQuery(mutation, { alarmId }, token)
    return true
  } catch (error) {
    console.error(`Failed to escalate alarm ${alarmId}:`, error)
    return false
  }
}

export async function deleteAlarm(alarmId: string, token: string): Promise<boolean> {
  try {
    const mutation = `
      mutation DeleteAlarm($alarmId: String!) {
        deleteAlarm(alarmId: $alarmId) {
          id
        }
      }
    `
    
    await graphqlQuery(mutation, { alarmId }, token)
    return true
  } catch (error) {
    console.error(`Failed to delete alarm ${alarmId}:`, error)
    return false
  }
}

// Device Events API functions (using Strata47 SDK)
export async function getDeviceEvents(
  token: string,
  deviceId?: string,
  createdAfter?: Date,
  limit: number = 100
): Promise<DeviceEventData[]> {
  try {
    const apiInstance = new DeviceEventsApi(apiTokenConfiguration(token))
    
    if (deviceId) {
      // Get events for a specific device
      const req: ListDeviceEventsRequest = {
        perPage: limit,
        ...(createdAfter && { createdAfter })
      }
      console.log('📋 DeviceEventsApi: Fetching events for device:', deviceId, 'with request:', req)
      
      const response = await apiInstance.listDeviceEventsById({ deviceId, ...req })
      console.log('📋 DeviceEventsApi: Raw response for device events:', response)
      console.log('📋 DeviceEventsApi: Response type:', typeof response)
      console.log('📋 DeviceEventsApi: Response keys:', response ? Object.keys(response) : 'null response')
      
      if (response && 'items' in response && Array.isArray((response as any).items)) {
        const items = (response as any).items
        console.log('📋 DeviceEventsApi: Found', items.length, 'events for device')
        console.log('📋 DeviceEventsApi: First event sample:', items.length > 0 ? items[0] : 'No events')
        
        return items.map((event: DeviceEvent): DeviceEventData => ({
          eventId: event.eventId || `${event.deviceId}-${Date.now()}`,
          deviceId: event.deviceId || deviceId,
          deviceName: event.deviceName,
          level: event.level || 'info',
          message: event.message || 'Device event',
          timestamp: (event as any).createdAt || (event as any).timestamp || new Date().toISOString(),
          locationId: event.locationId,
          locationName: event.locationName,
          deviceTypeId: event.deviceTypeId,
          deviceTypeName: event.deviceTypeName
        }))
      } else {
        console.log('📋 DeviceEventsApi: No items array found in response for device events')
      }
    } else {
      // Get events for all devices in namespace
      const req: ListDeviceEventsRequest = {
        perPage: limit,
        ...(createdAfter && { createdAfter })
      }
      console.log('📋 DeviceEventsApi: Fetching all namespace events with request:', req)
      
      const response = await apiInstance.listDeviceEvents(req)
      console.log('📋 DeviceEventsApi: Raw response for namespace events:', response)
      console.log('📋 DeviceEventsApi: Response type:', typeof response)
      console.log('📋 DeviceEventsApi: Response keys:', response ? Object.keys(response) : 'null response')
      
      if (response && 'items' in response && Array.isArray((response as any).items)) {
        const items = (response as any).items
        console.log('📋 DeviceEventsApi: Found', items.length, 'events in namespace')
        console.log('📋 DeviceEventsApi: First event sample:', items.length > 0 ? items[0] : 'No events')
        if (items.length > 0) {
          console.log('📋 DeviceEventsApi: Event fields available:', Object.keys(items[0]))
        }
        
        return items.map((event: DeviceEvent): DeviceEventData => ({
          eventId: event.eventId || `${event.deviceId}-${Date.now()}`,
          deviceId: event.deviceId || 'unknown',
          deviceName: event.deviceName,
          level: event.level || 'info',
          message: event.message || 'Device event',
          timestamp: (event as any).createdAt || (event as any).timestamp || new Date().toISOString(),
          locationId: event.locationId,
          locationName: event.locationName,
          deviceTypeId: event.deviceTypeId,
          deviceTypeName: event.deviceTypeName
        }))
      } else {
        console.log('📋 DeviceEventsApi: No items array found in response for namespace events')
        console.log('📋 DeviceEventsApi: Checking for direct array response...')
        if (Array.isArray(response)) {
          console.log('📋 DeviceEventsApi: Response is direct array with length:', response.length)
          return response.map((event: DeviceEvent): DeviceEventData => ({
            eventId: event.eventId || `${event.deviceId}-${Date.now()}`,
            deviceId: event.deviceId || 'unknown',
            deviceName: event.deviceName,
            level: event.level || 'info',
            message: event.message || 'Device event',
            timestamp: (event as any).createdAt || (event as any).timestamp || new Date().toISOString(),
            locationId: event.locationId,
            locationName: event.locationName,
            deviceTypeId: event.deviceTypeId,
            deviceTypeName: event.deviceTypeName
          }))
        }
      }
    }
    
    console.log('📋 DeviceEventsApi: No valid events found, returning empty array')
    return []
  } catch (error) {
    console.error('📋 DeviceEventsApi: Failed to fetch device events:', error)
    console.error('📋 DeviceEventsApi: Error details:', error instanceof Error ? error.message : String(error))
    return []
  }
}

// Get all device events for the current namespace, sorted by timestamp (newest first)
export async function getAllDeviceEventsForNamespace(token: string, limit: number = 100): Promise<DeviceEventData[]> {
  try {
    console.log('📋 getAllDeviceEventsForNamespace: Starting fetch with limit:', limit)
    const events = await getDeviceEvents(token, undefined, undefined, limit)
    
    console.log('📋 getAllDeviceEventsForNamespace: Retrieved', events.length, 'events from API')
    if (events.length > 0) {
      console.log('📋 getAllDeviceEventsForNamespace: Sample event:', events[0])
      console.log('📋 getAllDeviceEventsForNamespace: Event levels found:', [...new Set(events.map(e => e.level))])
    }
    
    // Sort by timestamp, newest first
    const sortedEvents = events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    console.log('📋 getAllDeviceEventsForNamespace: Returning', sortedEvents.length, 'sorted events')
    
    return sortedEvents
  } catch (error) {
    console.error('📋 getAllDeviceEventsForNamespace: Failed to fetch namespace device events:', error)
    return []
  }
}

// Device Commands API functions (using Strata47 SDK)
export async function sendDeviceCommand(
  token: string,
  deviceId: string,
  commandData: string,
  description?: string
): Promise<boolean> {
  try {
    const apiInstance = new DeviceCommandsApi(apiTokenConfiguration(token))

    // Parse commandData string to object - the API expects an object, not a string
    // Even though the SDK types it as string, it needs to be sent as object in the JSON body
    let commandDataObj: any
    try {
      commandDataObj = JSON.parse(commandData)
    } catch (e) {
      console.error('📡 DeviceCommandsApi: Failed to parse commandData JSON:', e)
      console.error('📡 DeviceCommandsApi: commandData value:', commandData)
      throw new Error('Invalid commandData JSON')
    }

    const commandPayload: DeviceCommandCreateUpdate = {
      commandSequence: Date.now() % 2147483647, // Use timestamp modulo max INT to fit in PostgreSQL INTEGER column
      commandData: commandDataObj as any,  // Cast to any since SDK type is wrong - API expects object
      description: description,
      // sendAfter: optional - could be used for scheduled commands
    }

    console.log('📡 DeviceCommandsApi: Sending command to device:', deviceId)
    console.log('📡 DeviceCommandsApi: Command payload:', JSON.stringify(commandPayload, null, 2))
    console.log('📡 DeviceCommandsApi: API endpoint:', Config.STRATA47_API_URL)

    const response = await apiInstance.createDeviceCommand({
      deviceId: deviceId,
      deviceCommandCreateUpdate: commandPayload
    })

    console.log('📡 DeviceCommandsApi: Command sent successfully')
    console.log('📡 DeviceCommandsApi: Response:', typeof response, Object.keys(response || {}))
    return true

  } catch (error: any) {
    console.error('📡 DeviceCommandsApi: Failed to send command')
    console.error('📡 DeviceCommandsApi: Error type:', error?.constructor?.name)
    console.error('📡 DeviceCommandsApi: Error message:', error?.message)
    console.error('📡 DeviceCommandsApi: Error response:', error?.response)
    console.error('📡 DeviceCommandsApi: Error body:', error?.body)
    console.error('📡 DeviceCommandsApi: Full error:', JSON.stringify(error, null, 2))
    console.error('📡 DeviceCommandsApi: Device ID:', deviceId)
    console.error('📡 DeviceCommandsApi: Command data:', commandData)

    // Try to get more details from the error
    if (error?.response) {
      try {
        const errorText = await error.response.text()
        console.error('📡 DeviceCommandsApi: Error response text:', errorText)
      } catch (e) {
        console.error('📡 DeviceCommandsApi: Could not read error response text')
      }
    }

    return false
  }
}

// Specific Pascal device command functions
// The firmware expects command data as JSON with field names like "acknowledge" or "mute"
// and values like "ACK_ALL_ALARMS" or "MUTE_ALL_ALARMS"
// See pascal-io-bridge/main/commands.c lines 37-45 and 189-220
export async function sendAckCommand(token: string, deviceId: string): Promise<boolean> {
  // Pass object directly, not stringified - SDK will handle serialization
  const commandData = {
    acknowledge: 'ACK_ALL_ALARMS'
  }
  return sendDeviceCommand(
    token,
    deviceId,
    JSON.stringify(commandData),  // SDK expects string, will parse to object internally
    'Acknowledge all device alarms from mobile app'
  )
}

export async function sendMuteCommand(token: string, deviceId: string): Promise<boolean> {
  // Pass object directly, not stringified - SDK will handle serialization
  const commandData = {
    mute: 'MUTE_ALL_ALARMS'
  }
  return sendDeviceCommand(
    token,
    deviceId,
    JSON.stringify(commandData),  // SDK expects string, will parse to object internally
    'Mute all device alarms from mobile app'
  )
}

export async function sendEscalateCommand(token: string, deviceId: string): Promise<boolean> {
  try {
    console.log(`🚀 Escalating alarm for device ${deviceId}`)

    // First, get the active escalations for this device (check both acknowledged and unacknowledged)
    const query = `
      query GetActiveEscalations($deviceId: String!) {
        activeEscalations(deviceId: $deviceId) {
          id
          deviceId
          currentLevel
          maxLevelReached
          isResolved
          isAcknowledged
        }
      }
    `

    const result = await graphqlQuery(query, { deviceId }, token)
    const escalations = result.activeEscalations || []

    if (escalations.length === 0) {
      console.warn('⚠️ No active escalation found for device')
      console.warn('⚠️ The escalation system requires an escalation policy to be configured')
      console.warn('⚠️ Escalations are automatically created when alarms occur if a policy exists')
      alert('No escalation policy is configured for this device.\n\nEscalation policies must be set up in the admin console before escalations can be triggered.')
      return false
    }

    const escalation = escalations[0]
    console.log(`✅ Found escalation ${escalation.id} at level ${escalation.currentLevel}`)

    // Now force escalate to next level
    const escalateMutation = `
      mutation ForceEscalate($escalationId: String!) {
        forceEscalateToNextLevel(escalationId: $escalationId) {
          id
          currentLevel
          maxLevelReached
        }
      }
    `

    const escalateResult = await graphqlQuery(escalateMutation, { escalationId: escalation.id }, token)
    const updatedEscalation = escalateResult.forceEscalateToNextLevel

    console.log(`✅ Escalation successful: level ${escalation.currentLevel} -> ${updatedEscalation.currentLevel}`)
    alert(`Escalation advanced from level ${escalation.currentLevel} to level ${updatedEscalation.currentLevel}.\n\nNotifications will be sent to the next escalation tier.`)
    return true

  } catch (error) {
    console.error('❌ Error escalating alarm:', error)
    if (error instanceof Error && error.message.includes('maximum escalation level')) {
      alert('This alarm is already at the maximum escalation level.\n\nNo further escalation is possible.')
    } else if (error instanceof Error && error.message.includes('Cannot escalate a resolved alert')) {
      alert('This alarm has already been resolved.\n\nResolved alarms cannot be escalated.')
    } else {
      alert('Failed to escalate alarm.\n\nPlease check that an escalation policy is configured and try again.')
    }
    return false
  }
}

// Device Serial Number Lookup (GraphQL)
export async function getDeviceIdBySerial(serialNumber: string, token: string): Promise<string | null> {
  try {
    console.log('🔍 Looking up deviceId for serial:', serialNumber)

    // Use .replace() to remove escapes that might be added during build
    const query = `
      query GetDeviceBySerial($serial: String!) {
        deviceSerial(serial: $serial) {
          deviceId
          serial
        }
      }
    `.replace(/\\/g, '')

    const result = await graphqlQuery(query, { serial: serialNumber }, token)
    console.log('🔍 GraphQL result:', JSON.stringify(result, null, 2))

    if (result.deviceSerial && result.deviceSerial.deviceId) {
      console.log('✅ Found deviceId for serial:', serialNumber, '→', result.deviceSerial.deviceId)
      return result.deviceSerial.deviceId
    }

    console.warn('⚠️ No device found for serial:', serialNumber)
    console.warn('⚠️ Result structure:', result)
    return null
  } catch (error) {
    console.error('❌ Failed to lookup device by serial:', serialNumber)
    console.error('❌ Error details:', error)
    return null
  }
}

// Check if escalation policy exists for a device/location
export async function checkEscalationPolicyExists(
  token: string,
  namespaceId: string,
  deviceId?: string,
  locationId?: string
): Promise<boolean> {
  try {
    // The API uses scopeType and scopeId, not deviceId/locationId directly
    // For device-level policies: scopeType=DEVICE, scopeId=deviceId (note: enum values are UPPERCASE)
    // For location-level policies: scopeType=SITE, scopeId=locationId
    const query = `
      query CheckEscalationPolicy($namespaceId: String!, $scopeType: EscalationScopeType, $scopeId: String, $includeInherited: Boolean) {
        escalationPolicies(namespaceId: $namespaceId, scopeType: $scopeType, scopeId: $scopeId, includeInherited: $includeInherited) {
          id
          isActive
          scopeType
          scopeId
        }
      }
    `

    // Check for policies at multiple levels with inheritance
    const variables: any = {
      namespaceId,
      includeInherited: true // Include inherited policies from parent scopes
    }

    // Priority: device/project-specific > site/location-specific > organization-wide
    // Note: GraphQL enum values are UPPERCASE: ORGANIZATION, SITE, PROJECT
    // Devices fall under PROJECT scope in the escalation hierarchy
    if (deviceId) {
      variables.scopeType = 'PROJECT'
      variables.scopeId = deviceId
    } else if (locationId) {
      variables.scopeType = 'SITE'
      variables.scopeId = locationId
    }
    // If neither deviceId nor locationId, query will return organization-wide policies

    const result = await graphqlQuery(query, variables, token)

    // Check if any active policies exist
    const policies = result.escalationPolicies || []
    const hasActivePolicy = policies.some((p: any) => p.isActive)

    console.log(`📋 Escalation policy check - namespaceId: ${namespaceId}, scopeType: ${variables.scopeType || 'organization'}, scopeId: ${variables.scopeId || 'N/A'} → ${hasActivePolicy ? 'EXISTS' : 'NOT FOUND'}`)
    console.log(`📋 Found ${policies.length} policies:`, policies.map((p: any) => `${p.scopeType}:${p.scopeId} (${p.isActive ? 'active' : 'inactive'})`))

    return hasActivePolicy
  } catch (error) {
    console.error('Failed to check escalation policy:', error)
    return false
  }
}